import { formatSiteDate } from "@/lib/site-time";
/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminNav } from "@/components/admin/AdminNav";
import { adminPagesQuery, localeReadinessQuery } from "@/lib/content-queries";
import { PAGE_KEYS } from "@/lib/users.functions";

export const Route = createFileRoute("/_authenticated/admin/paginas/")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Pagina's beheren — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: AdminPagesPage,
});

const LABELS: Record<string, string> = {
  home: "Homepagina",
  nieuwbouw: "Nieuwbouw",
  renovatie: "Renovatie",
  technieken: "Technieken & lekdetectie",
  "warmtepompen-afdekkingen": "Warmtepompen & afdekkingen",
  realisaties: "Realisaties",
  "opbouw-zwembad": "Opbouw A-Z",
  "over-ons": "Over ons",
  offerte: "Offerte",
  blog: "Blog",
};

function AdminPagesPage() {
  const { data, isLoading, isError } = useQuery(adminPagesQuery);
  const readiness = useQuery(localeReadinessQuery);


  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <AdminNav />
      <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Pagina's</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Zolang een pagina niet gepubliceerd is, toont de website de tekst uit de code. Dat blijft de veilige terugval.
      </p>

      {isLoading && (
        <div className="mt-8 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Card className="mt-8">
          <CardContent className="p-6 text-sm text-destructive">
            De pagina's konden niet geladen worden. Herlaad de pagina of probeer later opnieuw.
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {readiness.data && (
            <Card className="mt-8">
              <CardContent className="p-4 text-sm">
                <p className="font-semibold">Stand van de vertalingen</p>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  {readiness.data.map((rij) => (
                    <li key={rij.lang}>
                      <span className="font-medium uppercase">{rij.lang}</span>: {rij.gepubliceerd} van {rij.totaal}{" "}
                      pagina's gepubliceerd —{" "}
                      {rij.volledig
                        ? "volledig, staat in de sitemap en mag geïndexeerd worden"
                        : `nog niet volledig, blijft op noindex${rij.ontbrekend.length > 0 ? ` (ontbreekt: ${rij.ontbrekend.join(", ")})` : ""}`}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <div className="mt-4 space-y-3">
            {PAGE_KEYS.map((sleutel) => {
              const row = data.find((item) => item.sleutel === sleutel && item.lang === "nl");
              return (
                <Card key={sleutel}>
                  <CardContent className="flex flex-wrap items-center gap-4 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={row?.status === "gepubliceerd" ? "default" : "secondary"}>
                          {row?.status === "gepubliceerd" ? "Online" : "Concept"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">Taal nl</span>
                        <span className="text-xs text-muted-foreground">
                          {row?.status === "gepubliceerd" ? "Inhoud uit de databank" : "Valt terug op de code"}
                        </span>
                        {row && (
                          <span className="text-xs text-muted-foreground">
                            Gewijzigd op {formatSiteDate(row.updatedAt)}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-semibold">{LABELS[sleutel] ?? sleutel}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {(["en", "fr"] as const)
                          .map((lang) => {
                            const vertaling = data.find((item) => item.sleutel === sleutel && item.lang === lang);
                            if (!vertaling) return `${lang.toUpperCase()}: niet vertaald`;
                            return `${lang.toUpperCase()}: ${vertaling.status === "gepubliceerd" ? "online" : "concept"}`;
                          })
                          .join(" · ")}
                      </p>
                    </div>
                    <Button asChild size="sm">
                      <Link to="/admin/paginas/$sleutel" params={{ sleutel }}>
                        <Pencil className="mr-1 h-4 w-4" /> Bewerken
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

    </main>
  );
}
